import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import CalloutMediaElement from '../common/Callout/CalloutMediaElement';
import {
    CardContainer,
    CardTitle,
    CardBody,
    CardContentContainer,
    CardTitleAndBodyContainer,
    sizeStyles,
    HBCalloutCardTheme
} from './HBCalloutCard.styles';
import HBCloseButton from '../HBCloseButton/HBCloseButton';
import { HBCalloutCardProps } from './HBCalloutCard.types';
import { SizeProvider } from '../common/SizeProvider';
import { HBCalloutFooter } from '../HBCalloutFooter/HBCalloutFooter';

function useHBCalloutCardTheme() {
    return useTheme() as HBCalloutCardTheme;
}

function CalloutCardContent({
    title,
    body,
    steps,
    currentStep
}: Pick<HBCalloutCardProps, 'title' | 'body' | 'steps'> & {
    currentStep: number;
}) {
    const { size } = useHBCalloutCardTheme();

    let _title, _body;
    if (steps) {
        _title = steps[currentStep].title;
        _body = steps[currentStep].body;
    } else {
        _title = title;
        _body = body;
    }

    return (
        <CardTitleAndBodyContainer>
            <CardTitle type={sizeStyles[size].title}>{_title}</CardTitle>
            <CardBody type={sizeStyles[size].body}>{_body}</CardBody>
        </CardTitleAndBodyContainer>
    );
}

const HBCalloutCard: React.FC<HBCalloutCardProps> = ({
    title,
    body,
    size,
    layout,
    onDismiss,
    mediaPath,
    imageFit = 'cover',
    imageWidth = 200,
    withImagePadding,
    footerProps,
    steps,
    onImageClick,
    autoRotate = false,
    autoRotateInterval = 5000,
    autoRotateResumeDelay = 15000
}) => {
    // this is only relevant if steps are passed
    const [currentStep, setCurrentStep] = useState(0);
    const [isHovering, setIsHovering] = useState(false);

    return (
        <SizeProvider
            size={size}
            additionalThemeProps={{
                layout,
                hasImage: !!mediaPath,
                withImagePadding
            }}
        >
            <CardContainer
                onMouseEnter={
                    autoRotate ? () => setIsHovering(true) : undefined
                }
                onMouseLeave={
                    autoRotate ? () => setIsHovering(false) : undefined
                }
            >
                <CalloutMediaElement
                    imageFit={imageFit}
                    mediaPath={mediaPath}
                    width={imageWidth}
                    layout={layout}
                    withImagePadding={withImagePadding}
                    onClick={onImageClick}
                    currentStep={currentStep}
                    steps={steps}
                />

                <CardContentContainer>
                    <CalloutCardContent
                        title={title}
                        body={body}
                        steps={steps}
                        currentStep={currentStep}
                    />
                    <HBCalloutFooter
                        {...footerProps}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        steps={steps}
                        autoRotate={autoRotate}
                        autoRotateInterval={autoRotateInterval}
                        autoRotateResumeDelay={autoRotateResumeDelay}
                        isHovering={isHovering}
                    />
                </CardContentContainer>

                {onDismiss && (
                    <HBCloseButton
                        size="xs"
                        theme="dark"
                        distance={2}
                        testIdName="dismiss-button"
                        onClick={onDismiss}
                    />
                )}
            </CardContainer>
        </SizeProvider>
    );
};

export default HBCalloutCard;
